import { isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from "@angular/core";
import { Chart } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

export interface PieChartSegment {
  label: string;
  value: number;
  // Valor monetário opcional, exibido no tooltip.
  amount?: number;
}

// Paleta da marca (roxo, ciano, amarelo, cinza).
const DEFAULT_COLORS = ["#7C0084", "#00C6E3", "#FFED3B", "#828282"];

// Mesma fonte do app (Poppins) para o gráfico não destoar do restante da UI.
const FONT_FAMILY = '"Poppins", sans-serif';

@Component({
  selector: "app-pie-chart",
  imports: [],
  templateUrl: "./pie-chart.component.html",
  styleUrl: "./pie-chart.component.scss",
})
export class PieChartComponent implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private chart?: Chart;
  private viewReady = false;

  @Input({ required: true }) canvasId = "";
  @Input() colors: string[] = DEFAULT_COLORS;
  // Sufixo opcional para os valores (ex.: "%"). Só aplica quando o segmento não
  // tem `amount` (que é formatado como moeda).
  @Input() valueSuffix = "";

  private _segments: PieChartSegment[] = [];
  @Input({ required: true }) set segments(segments: PieChartSegment[]) {
    this._segments = segments ?? [];
    // Só desenha depois que o <canvas> existe no DOM (ngAfterViewInit).
    if (this.viewReady) {
      this.updateChart();
    }
  }
  get segments(): PieChartSegment[] {
    return this._segments;
  }

  get hasData(): boolean {
    return this._segments.some((segment) => segment.value > 0);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.updateChart();
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private updateChart(): void {
    if (!isPlatformBrowser(this.platformId) || !this.canvasId) {
      return;
    }

    Chart.getChart(this.canvasId)?.destroy();

    if (!this.hasData) {
      return;
    }

    const segments = this._segments;
    const valueSuffix = this.valueSuffix;

    this.chart = new Chart(this.canvasId, {
      type: "pie",
      data: {
        labels: segments.map((segment) => segment.label),
        datasets: [
          {
            data: segments.map((segment) => segment.value),
            backgroundColor: this.colors,
            hoverOffset: 4,
          },
        ],
      },
      plugins: [ChartDataLabels],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              font: { family: FONT_FAMILY, size: 13 },
            },
          },
          datalabels: {
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            borderRadius: 4,
            padding: 4,
            color: "#333",
            font: { weight: "bold", family: FONT_FAMILY },
            formatter: (_value, context) => {
              const segment = segments[context.dataIndex];
              if (segment.amount !== undefined) {
                return segment.amount.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                });
              }
              return `${segment.value}${valueSuffix}`;
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const segment = segments[context.dataIndex];
                let text = `${segment.label}: ${segment.value}${valueSuffix}`;
                if (segment.amount !== undefined) {
                  text += ` • ${segment.amount.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}`;
                }
                return text;
              },
            },
          },
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }) as unknown as any;

    // O canvas pode ser desenhado antes da fonte web (Poppins) terminar de
    // carregar; quando ela ficar pronta, redesenha para aplicar a fonte certa.
    if (typeof document !== "undefined" && document.fonts?.ready) {
      void document.fonts.ready.then(() => this.chart?.update());
    }
  }
}
